import type { QueryType } from '../lib/queryParser';
import type { AIAnswerResult } from '../lib/mixpanel';

interface TopDaysResult {
  date: string;
  count: number;
}

interface BreakdownResult {
  name: string;
  count: number;
}

interface TrendResult {
  series: string[];
  values: Record<string, Record<string, number>>;
}

interface AnomalyResult {
  anomalies: Array<{
    date: string;
    value: number;
    zscore: number;
    type: 'spike' | 'drop';
  }>;
  mean: number;
  stdDev: number;
  dailyValues: Array<{ date: string; value: number }>;
}

interface TopUsersResult {
  rank: number;
  userId: string;
  eventCount: number;
  daysActive: number;
}

interface AIInsight {
  type: 'opportunity' | 'warning' | 'observation';
  title: string;
  description: string;
  action?: string;
}

interface AIInsightsResult {
  insights: AIInsight[];
  summary: string;
  generatedAt: string;
  mode: 'local' | 'api';
}

export interface QueryResultData {
  type: QueryType;
  question: string;
  data: TopDaysResult[] | BreakdownResult[] | TrendResult | AnomalyResult | TopUsersResult[] | AIInsightsResult | AIAnswerResult;
  timestamp: string;
}

interface QueryResultProps {
  result: QueryResultData;
  onDismiss?: () => void;
}

function isTopDaysResult(data: unknown): data is TopDaysResult[] {
  return Array.isArray(data) && data.every((item) => typeof item === 'object' && item !== null && 'date' in item);
}

function isBreakdownResult(data: unknown): data is BreakdownResult[] {
  return Array.isArray(data) && data.every((item) => typeof item === 'object' && item !== null && 'name' in item);
}

function isTrendResult(data: unknown): data is TrendResult {
  return typeof data === 'object' && data !== null && 'series' in data && 'values' in data;
}

function isAnomalyResult(data: unknown): data is AnomalyResult {
  return typeof data === 'object' && data !== null && 'anomalies' in data && 'mean' in data;
}

function isTopUsersResult(data: unknown): data is TopUsersResult[] {
  return Array.isArray(data) && data.every((item) => typeof item === 'object' && item !== null && 'userId' in item);
}

function isAIInsightsResult(data: unknown): data is AIInsightsResult {
  return typeof data === 'object' && data !== null && 'insights' in data && 'mode' in data;
}

function isAIAnswerResult(data: unknown): data is AIAnswerResult {
  return typeof data === 'object' && data !== null && 'answer' in data && 'mode' in data;
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function QueryResult({ result, onDismiss }: QueryResultProps) {
  const { type, question, data } = result;

  // Top Days table
  if (isTopDaysResult(data)) {
    if (data.length === 0) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs text-blue-700 font-medium">{question}</div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-blue-400 hover:text-blue-600 text-sm">×</button>
            )}
          </div>
          <div className="text-xs text-blue-700">
            No days found for this range.
          </div>
        </div>
      );
    }

    const maxCount = Math.max(...data.map(d => d.count));
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
        <div className="flex items-start justify-between mb-2">
          <div className="text-xs text-blue-700 font-medium">{question}</div>
          {onDismiss && (
            <button onClick={onDismiss} className="text-blue-400 hover:text-blue-600 text-sm">×</button>
          )}
        </div>
        <div className="space-y-1">
          {data.slice(0, 10).map((item, i) => (
            <div key={item.date} className="flex items-center gap-2 text-xs">
              <span className="w-4 text-blue-400">{i + 1}.</span>
              <span className="w-16 text-gray-700">{formatDate(item.date)}</span>
              <div className="flex-1 h-4 bg-blue-100 rounded overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded"
                  style={{ width: `${(item.count / maxCount) * 100}%` }}
                />
              </div>
              <span className="w-12 text-right text-gray-600">{formatNumber(item.count)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Breakdown (by country/city) list
  if (isBreakdownResult(data)) {
    if (data.length === 0) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs text-green-700 font-medium">{question}</div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-green-400 hover:text-green-600 text-sm">×</button>
            )}
          </div>
          <div className="text-xs text-green-700">
            No matching segments in this time range.
          </div>
        </div>
      );
    }

    const total = data.reduce((sum, d) => sum + d.count, 0);
    const maxCount = Math.max(...data.map(d => d.count));
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
        <div className="flex items-start justify-between mb-2">
          <div className="text-xs text-green-700 font-medium">{question}</div>
          {onDismiss && (
            <button onClick={onDismiss} className="text-green-400 hover:text-green-600 text-sm">×</button>
          )}
        </div>
        <div className="space-y-1">
          {data.slice(0, 10).map((item, i) => {
            const pct = Math.round((item.count / total) * 100);
            return (
              <div key={item.name} className="flex items-center gap-2 text-xs">
                <span className="w-4 text-green-400">{i + 1}.</span>
                <span className="w-20 text-gray-700 truncate" title={item.name}>
                  {item.name || '(unknown)'}
                </span>
                <div className="flex-1 h-4 bg-green-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded"
                    style={{ width: `${(item.count / maxCount) * 100}%` }}
                  />
                </div>
                <span className="w-12 text-right text-gray-600">{formatNumber(item.count)}</span>
                <span className="w-8 text-right text-gray-400">{pct}%</span>
              </div>
            );
          })}
        </div>
        {data.length > 10 && (
          <div className="text-xs text-green-600 mt-2">
            +{data.length - 10} more
          </div>
        )}
      </div>
    );
  }

  // Trend (mini sparkline representation)
  if (isTrendResult(data)) {
    const seriesKeys = Object.keys(data.values);

    if (seriesKeys.length === 0) {
      return (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs text-purple-700 font-medium">{question}</div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-purple-400 hover:text-purple-600 text-sm">×</button>
            )}
          </div>
          <div className="text-xs text-purple-700">No events returned for this query.</div>
        </div>
      );
    }

    const eventName = seriesKeys[0];
    const showComparisonLayout = type === 'compare' && seriesKeys.length > 1;
    const showAnomalySummary = type === 'anomaly';

    if (showComparisonLayout) {
      const globalMax = Math.max(
        ...seriesKeys.flatMap((key) => Object.values(data.values[key] || {})),
        1
      );

      return (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs text-purple-700 font-medium">{question}</div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-purple-400 hover:text-purple-600 text-sm">×</button>
            )}
          </div>
          <div className="space-y-2">
            {seriesKeys.map((key) => {
              const values = data.values[key] || {};
              const sortedDates = Object.keys(values).sort();
              const recentDates = sortedDates.slice(-30);
              const recentCounts = recentDates.map(date => values[date]);
              const total = recentCounts.reduce((sum, value) => sum + value, 0);
              const avg = recentCounts.length ? Math.round(total / recentCounts.length) : 0;

              return (
                <div key={key} className="bg-white/50 border border-purple-100 rounded-lg p-2">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span className="font-medium text-gray-700">{key || '(unknown)'}</span>
                    <span>{formatNumber(total)} total</span>
                  </div>
                  <div className="flex items-end gap-px h-12 mb-1">
                    {recentCounts.length === 0 ? (
                      <div className="text-[10px] text-gray-400">No daily data</div>
                    ) : (
                      recentCounts.map((count, i) => (
                        <div
                          key={`${key}-${recentDates[i] ?? i}`}
                          className="flex-1 bg-purple-400 rounded-t min-w-[2px]"
                          style={{ height: `${(count / (globalMax || 1)) * 100}%` }}
                          title={`${recentDates[i]}: ${count}`}
                        />
                      ))
                    )}
                  </div>
                  <div className="text-[10px] text-gray-500 flex justify-between">
                    <span>Avg {formatNumber(avg)}/day</span>
                    <span>{recentCounts.length} days</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (showAnomalySummary) {
      return (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs text-purple-700 font-medium">{question}</div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-purple-400 hover:text-purple-600 text-sm">×</button>
            )}
          </div>
          <div className="text-xs text-gray-600">
            Anomaly details are shown above. Try asking about specific dates or segments for deeper context.
          </div>
        </div>
      );
    }
    const primaryEvent = seriesKeys[0];
    const values = data.values[primaryEvent] || {};
    const sortedDates = Object.keys(values).sort();
    const recentDates = sortedDates.slice(-30);
    const recentCounts = recentDates.map(date => values[date]);
    const maxVal = Math.max(...recentCounts, 1);
    const total = recentCounts.reduce((sum, value) => sum + value, 0);
    const avg = recentCounts.length ? Math.round(total / recentCounts.length) : 0;

    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
        <div className="flex items-start justify-between mb-2">
          <div className="text-xs text-purple-700 font-medium">{question}</div>
          {onDismiss && (
            <button onClick={onDismiss} className="text-purple-400 hover:text-purple-600 text-sm">×</button>
          )}
        </div>
        {recentCounts.length === 0 ? (
          <div className="text-xs text-gray-500">No daily activity yet.</div>
        ) : (
          <>
            <div className="flex items-end gap-px h-16 mb-2">
              {recentCounts.map((count, i) => (
                <div
                  key={recentDates[i] ?? i}
                  className="flex-1 bg-purple-400 rounded-t min-w-[2px]"
                  style={{ height: `${(count / maxVal) * 100}%` }}
                  title={`${recentDates[i]}: ${count}`}
                />
              ))}
            </div>

            <div className="flex justify-between text-xs text-gray-500">
              <span>Total: {formatNumber(total)}</span>
              <span>Avg: {formatNumber(avg)}/day</span>
              <span>{recentCounts.length} days</span>
            </div>
          </>
        )}
      </div>
    );
  }

  // Anomaly detection result
  if (isAnomalyResult(data)) {
    const hasAnomalies = data.anomalies.length > 0;
    return (
      <div className={`${hasAnomalies ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'} border rounded-lg p-3 mb-3`}>
        <div className="flex items-start justify-between mb-2">
          <div className={`text-xs font-medium ${hasAnomalies ? 'text-orange-700' : 'text-green-700'}`}>
            {question}
          </div>
          {onDismiss && (
            <button onClick={onDismiss} className={`${hasAnomalies ? 'text-orange-400 hover:text-orange-600' : 'text-green-400 hover:text-green-600'} text-sm`}>×</button>
          )}
        </div>

        {hasAnomalies ? (
          <>
            <div className="text-xs text-orange-800 mb-2">
              Found {data.anomalies.length} anomal{data.anomalies.length === 1 ? 'y' : 'ies'} (avg: {formatNumber(data.mean)}/day)
            </div>
            <div className="space-y-1">
              {data.anomalies.slice(0, 5).map((anomaly) => (
                <div key={anomaly.date} className="flex items-center gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-white ${anomaly.type === 'spike' ? 'bg-red-500' : 'bg-blue-500'}`}>
                    {anomaly.type === 'spike' ? '↑' : '↓'}
                  </span>
                  <span className="text-gray-700">{formatDate(anomaly.date)}</span>
                  <span className="text-gray-600">{formatNumber(anomaly.value)}</span>
                  <span className="text-gray-400">({anomaly.zscore > 0 ? '+' : ''}{anomaly.zscore}σ)</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-xs text-green-700">
            No significant anomalies detected. Mean: {formatNumber(data.mean)}/day, Std Dev: {formatNumber(data.stdDev)}
          </div>
        )}
      </div>
    );
  }

  // Top users leaderboard
  if (isTopUsersResult(data)) {
    if (data.length === 0) {
      return (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-3">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs text-indigo-700 font-medium">{question}</div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-indigo-400 hover:text-indigo-600 text-sm">×</button>
            )}
          </div>
          <div className="text-xs text-indigo-700">
            No active users found in this period.
          </div>
        </div>
      );
    }

    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-3">
        <div className="flex items-start justify-between mb-2">
          <div className="text-xs text-indigo-700 font-medium">{question}</div>
          {onDismiss && (
            <button onClick={onDismiss} className="text-indigo-400 hover:text-indigo-600 text-sm">×</button>
          )}
        </div>
        <div className="space-y-1">
          {data.map((user) => (
            <div key={user.userId} className="flex items-center gap-2 text-xs">
              <span className="w-4 text-indigo-400">{user.rank}.</span>
              <span className="flex-1 text-gray-700 truncate font-mono text-[10px]" title={user.userId}>
                {user.userId.slice(0, 20)}...
              </span>
              <span className="text-gray-600">{formatNumber(user.eventCount)} events</span>
              <span className="text-gray-400">{user.daysActive}d</span>
            </div>
          ))}
        </div>
        <div className="text-xs text-indigo-600 mt-2">
          Based on last 30 days of data
        </div>
      </div>
    );
  }

  // AI Insights
  if (isAIInsightsResult(data)) {
    const insightColors = {
      opportunity: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: '💡', text: 'text-emerald-700' },
      warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: '⚠️', text: 'text-amber-700' },
      observation: { bg: 'bg-sky-50', border: 'border-sky-200', icon: '📊', text: 'text-sky-700' },
    };

    return (
      <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-lg p-3 mb-3">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-base">✨</span>
            <div className="text-xs text-violet-700 font-medium">{question}</div>
          </div>
          {onDismiss && (
            <button onClick={onDismiss} className="text-violet-400 hover:text-violet-600 text-sm">×</button>
          )}
        </div>

        {data.summary && (
          <div className="text-xs text-gray-700 mb-3 italic">
            "{data.summary}"
          </div>
        )}

        <div className="space-y-2">
          {data.insights.map((insight, i) => {
            const colors = insightColors[insight.type] || insightColors.observation;
            return (
              <div
                key={i}
                className={`${colors.bg} ${colors.border} border rounded-lg p-2`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-sm">{colors.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium ${colors.text}`}>
                      {insight.title}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {insight.description}
                    </div>
                    {insight.action && (
                      <div className="text-xs text-gray-500 mt-1 italic">
                        → {insight.action}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between items-center mt-2 pt-2 border-t border-violet-100">
          <span className="text-[10px] text-violet-400">
            via {data.mode === 'local' ? 'Core (private)' : 'External AI'}
          </span>
          <span className="text-[10px] text-violet-400">
            {new Date(data.generatedAt).toLocaleTimeString()}
          </span>
        </div>
      </div>
    );
  }

  // Conversational answer
  if (isAIAnswerResult(data)) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
        <div className="flex items-start justify-between mb-2">
          <div className="text-xs text-slate-700 font-medium">{question}</div>
          {onDismiss && (
            <button onClick={onDismiss} className="text-slate-400 hover:text-slate-600 text-sm">×</button>
          )}
        </div>
        <div className="text-sm text-slate-800 whitespace-pre-line">
          {data.answer}
        </div>
        <div className="text-[10px] text-slate-400 mt-2 flex justify-between">
          <span>via {data.mode === 'local' ? 'Core (private)' : 'External AI'}</span>
          <span>{new Date(data.generatedAt).toLocaleTimeString()}</span>
        </div>
      </div>
    );
  }

  // Unknown result type
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
      <div className="flex items-start justify-between">
        <div className="text-xs text-gray-600">{question}</div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600 text-sm">×</button>
        )}
      </div>
      <div className="text-xs text-gray-500 mt-1">
        Unable to display result
      </div>
    </div>
  );
}
